import React from "react";
import { Box, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import dico from "../../../util/src/language/dico.js";
import type {
  SceneBehaviorRoute,
  SceneCatalogSection,
} from "./SceneBehaviorNavigator.js";

export interface SceneBehaviorBreadcrumbProps {
  route: SceneBehaviorRoute;
  entryName?: string;
  onNavigate: (route: SceneBehaviorRoute) => void;
}

function getSectionLabel(section: SceneCatalogSection): string {
  const d = dico().behavior.sceneCatalog;
  switch (section) {
    case "animations":
      return d.animations ?? "Animations";
    case "events":
      return d.events ?? "Events";
    case "functions":
      return d.functions ?? "Functions";
    case "objects":
      return d.objects;
    case "variables":
      return d.variables ?? "Variables";
  }
}

const Segment = ({
  label,
  clickable,
  onClick,
}: {
  label: string;
  clickable: boolean;
  onClick?: () => void;
}) => (
  <Typography
    component="span"
    variant="body2"
    onClick={clickable ? onClick : undefined}
    sx={{
      fontWeight: 600,
      cursor: clickable ? "pointer" : "default",
      color: clickable ? "primary.main" : "text.primary",
      "&:hover": clickable ? { textDecoration: "underline" } : {},
      userSelect: "none",
    }}
  >
    {label}
  </Typography>
);

const Separator = () => (
  <NavigateNextIcon
    sx={{
      fontSize: 16,
      color: "text.disabled",
      mx: 0.25,
      verticalAlign: "middle",
    }}
  />
);

const SceneBehaviorBreadcrumbComponent: React.FC<
  SceneBehaviorBreadcrumbProps
> = ({ route, entryName, onNavigate }) => {
  const d = (dico().behavior as Record<string, unknown>).sceneCatalog as
    | Record<string, string>
    | undefined;
  const sceneLabel = d?.breadcrumbScene ?? "Scene";

  const segments: React.ReactNode[] = [];

  const isHub = route.level === "hub";
  segments.push(
    <Segment
      key="scene"
      label={sceneLabel}
      clickable={!isHub}
      onClick={() => onNavigate({ level: "hub" })}
    />,
  );

  if (route.level === "list" || route.level === "detail") {
    const sectionLabel = getSectionLabel(route.section);
    const isLast = route.level === "list";
    segments.push(<Separator key="sep-1" />);
    segments.push(
      <Segment
        key="section"
        label={sectionLabel}
        clickable={!isLast}
        onClick={() => onNavigate({ level: "list", section: route.section })}
      />,
    );
  }

  if (route.level === "detail" && entryName) {
    segments.push(<Separator key="sep-2" />);
    segments.push(<Segment key="entry" label={entryName} clickable={false} />);
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.25,
        py: 0.75,
        px: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        flexShrink: 0,
      }}
    >
      {segments}
    </Box>
  );
};

export const SceneBehaviorBreadcrumb = React.memo(
  SceneBehaviorBreadcrumbComponent,
);
